import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { RewardType } from 'src/app/shared/models/constants/reward-type';
import { getVIPName } from 'src/app/shared/models/constants/vip-levels';
import { SelectorData } from 'src/app/utils/selectorDataType';
import { SelectedRegion } from '../checkbox-accordion/checkbox-accordion.component';

const TEXT_OVERFLOW_MAX_LENGTH = 32;

interface RewardInfoRow {
  display: boolean;
  label: string;
  values: string[];
}

export interface RewardInfoData {
  rewardType: SelectorData | null;
  relatedProductName: string | null;
  name: string | null;
  nameEn?: string | null;
  namePt: string | null;
  nameEs: string | null;
  redemptionLimit: number | null;
  rewardCost: number | null;
  rewardCode: string | null;
  extensionPeriod: number | null;
  vipLevels: number[] | null;
  vipOnly: boolean;
  families: any[];
  locationRestrictions: SelectedRegion[] | null;
}

@Component({
  selector: 'app-reward-info',
  templateUrl: './reward-info.component.html',
  styleUrls: ['./reward-info.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

/**
 * Component for displaying reward info
 */
export class RewardInfoComponent implements OnInit {
  readonly labels = {
    generalInfo: 'general-info',
    locationRestriction: 'location-restriction',
  };

  @Input() data!: RewardInfoData;

  @Input() availableFamilies!: any[];

  @Input() availableBusinessLevels!: any;

  generalInfoRows: RewardInfoRow[] = [];
  locationRestrictionRows: RewardInfoRow[] = [];
  language = '';

  /**
   * Constructor for RewardInfoComponent
   */
  constructor(private readonly translocoService: TranslocoService) {}

  /**
   * Lifecycle hook that is called after data-bound properties are initialized
   */
  ngOnInit() {
    this.language = this.translocoService.getActiveLang();
    this.generalInfoRows = [
      {
        display: true,
        label: this.translocoService.translate('related-with-a-product'),
        values: [
          this.data.relatedProductName ?? this.translocoService.translate('no'),
        ],
      },
      {
        display: this.shouldDisplayRewardTitles(),
        label: `${this.translocoService.translate(
          'reward-title'
        )} (${this.translocoService.translate('english')})`,
        values: [this.data.name!],
      },
      {
        display: this.shouldDisplayRewardTitles(),
        label: `${this.translocoService.translate(
          'reward-title'
        )} (${this.translocoService.translate('portuguese')})`,
        values: [this.data.namePt!],
      },
      {
        display: this.shouldDisplayRewardTitles(),
        label: `${this.translocoService.translate(
          'reward-title'
        )} (${this.translocoService.translate('spanish')})`,
        values: [this.data.nameEs!],
      },
      {
        display: true,
        label: this.translocoService.translate('redemption-limit-per-business'),
        values: [this.getRedemptionLimitLabel()],
      },
      {
        display: true,
        label: this.translocoService.translate('reward-code'),
        values: [this.data.rewardCode!],
      },
      {
        display: this.shouldDisplayExtensionPeriod(),
        label: this.translocoService.translate('extension-period'),
        values: [this.getExtensionPeriodLabel()],
      },
      {
        display: this.data.families !== undefined,
        label: this.translocoService.translate('printer-family'),
        values: this.getFamiliesLabels(),
      },
      {
        display: this.data.vipOnly,
        label: this.translocoService.translate('vip-only'),
        values: this.getVipOnlySeriesLabels(),
      },
    ];

    this.locationRestrictionRows = [
      {
        display: true,
        label: this.translocoService.translate('region-restrictions'),
        values: [
          this.getRestrictionRegions() ||
            this.translocoService.translate('none'),
        ],
      },
      {
        display: true,
        label: this.translocoService.translate('sub-region-restrictions'),
        values: [
          this.getRestrictionCountries() ||
            this.translocoService.translate('none'),
        ],
      },
    ];
  }

  /**
   * Returns redemption limit row's value with time label based on its value.
   * @return {string}
   */
  private getRedemptionLimitLabel() {
    if (!this.data.redemptionLimit) {
      return this.translocoService.translate('none');
    }

    const timeLabel =
      this.data.redemptionLimit === 1
        ? this.translocoService.translate('time')
        : this.translocoService.translate('times');

    return `${this.data.redemptionLimit} ${timeLabel}`;
  }

  /**
   * Returns extension period row's value with year label based on its value.
   * @return {string}
   */
  private getExtensionPeriodLabel() {
    const yearLabel =
      this.data.extensionPeriod === 1
        ? this.translocoService.translate('year')
        : this.translocoService.translate('years');

    return `${this.data.extensionPeriod} ${yearLabel}`;
  }

  /**
   * Returns families row's value.
   * @return {string[]}
   */
  private getFamiliesLabels(): string[] {
    const familiesLabels: string[] = [];

    if (this.data.families) {
      this.data.families.forEach((family) => {
        this.availableFamilies.forEach((availableFamily) => {
          if (family === availableFamily.value) {
            familiesLabels.push(availableFamily.label);
          }
        });
      });
    }

    return familiesLabels;
  }

  /**
   * Returns vip only row's value with series label based on its value.
   * @return {string[]}
   */
  private getVipOnlySeriesLabels(): string[] {
    const seriesLabels: string[] = [];

    if (this.availableBusinessLevels) {
      Object.entries(this.availableBusinessLevels).forEach(([key, _value]) => {
        let seriesResultString = 'Series ' + key + ': ';
        let hasAnyVipLevel = false;

        this.availableBusinessLevels[key].forEach((businessLevel: any) => {
          this.data.vipLevels?.forEach((vipLevel: any) => {
            if (vipLevel === businessLevel.id) {
              if (hasAnyVipLevel) {
                seriesResultString += ', ';
              }
              seriesResultString += getVIPName(key, businessLevel.level);
              hasAnyVipLevel = true;
            }
          });
        });

        if (hasAnyVipLevel) {
          seriesLabels.push(seriesResultString);
        }
      });
    }

    return seriesLabels;
  }

  /**
   * Returns a safe array of location restrictions.
   * @return {SelectedRegion[]}
   */
  private getLocationRestrictions(): SelectedRegion[] {
    return Array.isArray(this.data.locationRestrictions)
      ? this.data.locationRestrictions
      : [];
  }

  /**
   * Returns the restriction regions as a string.
   * @return {string}
   */
  private getRestrictionRegions(): string {
    return this.getLocationRestrictions()
      .filter((restrictions) => restrictions.allCountries)
      .map((restrictions) => restrictions.name)
      .join('; ');
  }

  /**
   * Returns the restriction countries as a string.
   * @return {string}
   */
  private getRestrictionCountries(): string {
    return this.getLocationRestrictions()
      .filter((restrictions) => !restrictions.allCountries)
      .map((restrictions) => restrictions.countries)
      .filter((countries) => countries.length > 0)
      .map((countries) => countries.map((country) => country.name).join('; '))
      .join('; ');
  }

  /**
   * Returns the restriction countries as a boolean.
   * @return {boolean}
   */
  private shouldDisplayExtensionPeriod(): boolean {
    return (
      this.data.rewardType!.value === RewardType.CUSTOMER_SERVICE_EXTENSION
    );
  }

  /**
   * Returns the restriction countries as a boolean.
   * @return {boolean}
   */
  private shouldDisplayRewardTitles(): boolean {
    return !this.data.relatedProductName;
  }

  /**
   * Returns the text content if the text has overflow for tooltip display.
   * @param {string} text
   * @return {string}
   */
  getTooltipContent(text: string): string {
    if (!text) return '';
    return text.length > TEXT_OVERFLOW_MAX_LENGTH ? text : '';
  }
}
